import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Globe2, Pencil, Phone, Plus, Trash2, type LucideIcon } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { getErrorMessage } from '@/lib/api'
import {
  orgTreeApi,
  type OrgTreeNodeSettingItem,
  type OrgTreeSettingCatalogOption,
} from '@/services/org-tree'

type NodeSettingManagerMode = 'social' | 'contact'

type NodeSettingFormState = {
  key: string
  itemKey: string
  value: string
}

const defaultFormState: NodeSettingFormState = {
  key: '',
  itemKey: '',
  value: '',
}

const toInputValue = (value?: string | string[] | null) => {
  if (Array.isArray(value)) {
    return value.join(', ')
  }

  return value ?? ''
}

const managerConfig: Record<NodeSettingManagerMode, { icon: LucideIcon; emptyTitle: string; emptyDescription: string }> = {
  social: {
    icon: Globe2,
    emptyTitle: 'No social media items configured',
    emptyDescription: 'Add a social media entry for this node.',
  },
  contact: {
    icon: Phone,
    emptyTitle: 'No contacts configured',
    emptyDescription: 'Add a contact method for this node.',
  },
}

type NodeSettingManagerProps = {
  nodeId: string | number
  mode: NodeSettingManagerMode
  title: string
  description: string
}

export default function NodeSettingManager({ nodeId, mode, title, description }: NodeSettingManagerProps) {
  const queryClient = useQueryClient()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [settingForm, setSettingForm] = useState<NodeSettingFormState>(defaultFormState)
  const [editingItem, setEditingItem] = useState<OrgTreeNodeSettingItem | null>(null)

  const currentConfig = managerConfig[mode]
  const Icon = currentConfig.icon

  const queryKey = mode === 'social' ? ['org-tree-social', nodeId] : ['org-tree-contacts', nodeId]

  const itemsQuery = useQuery({
    queryKey,
    queryFn: () => (mode === 'social' ? orgTreeApi.listSocialMedia(nodeId) : orgTreeApi.listContacts(nodeId)),
    enabled: Boolean(nodeId),
  })

  const catalogQuery = useQuery({
    queryKey: [mode === 'social' ? 'org-tree-social-types' : 'org-tree-contact-types'],
    queryFn: () => (mode === 'social' ? orgTreeApi.listSocialMediaTypes() : orgTreeApi.listContactTypes()),
    staleTime: 60_000,
  })

  const selectedOption = useMemo(
    () => (catalogQuery.data ?? []).find((item) => item.key === settingForm.key) ?? null,
    [catalogQuery.data, settingForm.key],
  )

  const selectedItem = useMemo(
    () => selectedOption?.items.find((item) => item.key === settingForm.itemKey) ?? null,
    [selectedOption, settingForm.itemKey],
  )

  const items = itemsQuery.data ?? []

  const resetDialog = () => {
    setIsDialogOpen(false)
    setSettingForm(defaultFormState)
    setEditingItem(null)
  }

  const invalidateItems = async () => {
    await queryClient.invalidateQueries({ queryKey })
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!settingForm.value.trim()) {
        throw new Error('Value is required')
      }

      if (editingItem) {
        return orgTreeApi.updateNodeSetting(editingItem.id, {
          node: nodeId,
          value: settingForm.value.trim(),
        })
      }

      if (!selectedOption || !selectedItem || selectedOption.data_type == null) {
        throw new Error('Type selection is required')
      }

      const payload = {
        node: nodeId,
        key: selectedOption.key,
        text: selectedOption.text,
        item_key: selectedItem.key,
        item_text: selectedItem.text,
        data_type: selectedOption.data_type,
        value: settingForm.value.trim(),
      }

      if (mode === 'social') {
        return orgTreeApi.createSocialMedia(payload)
      }

      return orgTreeApi.createContact(payload)
    },
    onSuccess: async () => {
      await invalidateItems()
      resetDialog()
      toast.success(editingItem ? `${title} updated successfully` : `${title} added successfully`)
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (settingId: string | number) => orgTreeApi.deleteNodeSetting(settingId),
    onSuccess: async () => {
      await invalidateItems()
      toast.success(`${title} deleted successfully`)
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const openCreate = () => {
    setEditingItem(null)
    setSettingForm(defaultFormState)
    setIsDialogOpen(true)
  }

  const openEdit = (item: OrgTreeNodeSettingItem) => {
    setEditingItem(item)
    setSettingForm({
      key: String(item.key ?? ''),
      itemKey: String(item.item_key ?? ''),
      value: toInputValue(item.value),
    })
    setIsDialogOpen(true)
  }

  return (
    <>
      <Card className="overflow-hidden rounded-[24px] border-border/80 bg-gradient-to-b from-background to-muted/10 shadow-sm">
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <Icon className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-xl">{title}</CardTitle>
                <CardDescription>{description}</CardDescription>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary">{items.length} configured</Badge>
              <Badge variant="outline">Catalog driven</Badge>
              {mode === 'social' ? <Badge variant="outline">Channel based</Badge> : <Badge variant="outline">Reachability</Badge>}
            </div>
          </div>
          <Button className="shadow-sm" onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Add {title}
          </Button>
        </CardHeader>
        <CardContent className="space-y-5">
          {itemsQuery.isLoading ? (
            <div className="rounded-3xl border border-dashed border-border/70 bg-muted/20 p-10 text-sm text-muted-foreground">
              Loading {title.toLowerCase()}...
            </div>
          ) : items.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-border/70 bg-linear-to-br from-primary/5 via-background to-muted/20 p-10 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <Icon className="h-7 w-7" />
              </div>
              <p className="font-medium text-foreground">{currentConfig.emptyTitle}</p>
              <p className="mt-2 text-sm text-muted-foreground">{currentConfig.emptyDescription}</p>
            </div>
          ) : (
            <>
              <div className="rounded-3xl border border-border/70 bg-background/90 p-5 shadow-sm">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">Configuration Notes</p>
                    <p className="mt-2 text-base font-semibold text-foreground">
                      {mode === 'social' ? 'Channels use backend-approved media keys.' : 'Contact records stay aligned with backend contact types.'}
                    </p>
                  </div>
                  <Badge variant="outline">Existing items update value only</Badge>
                </div>
              </div>
              <div className="overflow-hidden rounded-3xl border border-border/70 bg-background shadow-sm">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30 hover:bg-muted/30">
                    <TableHead className="pl-5">Type</TableHead>                    <TableHead>Key</TableHead>                    <TableHead>Value</TableHead>
                    <TableHead className="pr-5 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={String(item.id)}>
                      <TableCell className="pl-5">
                        <div className="flex flex-col gap-1">
                          <span className="font-medium text-foreground">{item.item_text || item.text || '-'}</span>
                          {item.text && item.item_text && item.text !== item.item_text ? <span className="text-xs text-muted-foreground">{item.text}</span> : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-xs text-muted-foreground">{item.item_key || item.key || '-'}</span>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        <div className="max-w-[28rem] rounded-xl bg-muted/20 px-3 py-2 text-foreground/85">
                          {toInputValue(item.value) || '-'}
                        </div>
                      </TableCell>
                      <TableCell className="pr-5">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(item)}>
                            <Pencil className="mr-1 h-4 w-4" />
                            Edit
                          </Button>
                          <Button variant="ghost" size="sm" className="text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => deleteMutation.mutate(item.id)} disabled={deleteMutation.isPending}>
                            <Trash2 className="mr-1 h-4 w-4" />
                            Delete
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={(open) => {
        if (!open) {
          resetDialog()
          return
        }

        setIsDialogOpen(true)
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingItem ? `Edit ${title}` : `Add ${title}`}</DialogTitle>
            <DialogDescription>
              {editingItem ? 'Only the value can be updated for an existing item.' : `Choose the ${title.toLowerCase()} type and value.`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-4">
            <div className="rounded-2xl border border-border/70 bg-muted/15 p-4">
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">Type Selection</p>
                {editingItem ? <Badge variant="outline">Locked on edit</Badge> : <Badge variant="secondary">Required</Badge>}
              </div>
              <div className="mt-4 grid gap-4">
                <div className="grid gap-2">
              <Label>{mode === 'social' ? 'Channel' : 'Category'}</Label>
              <Select
                value={settingForm.key}
                onValueChange={(value) => {
                  const option = (catalogQuery.data ?? []).find((item) => item.key === value)
                  setSettingForm({
                    key: value,
                    itemKey: option?.items[0]?.key ?? '',
                    value: editingItem ? settingForm.value : '',
                  })
                }}
                disabled={editingItem != null}
              >
                <SelectTrigger aria-label={mode === 'social' ? 'Channel' : 'Category'} className="w-full">
                  <SelectValue placeholder={mode === 'social' ? 'Choose social media' : 'Choose contact type'} />
                </SelectTrigger>
                <SelectContent>
                  {(catalogQuery.data ?? []).map((option: OrgTreeSettingCatalogOption) => (
                    <SelectItem key={option.key} value={option.key}>
                      {option.text}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {settingForm.key ? (
                <p className="text-xs text-muted-foreground">Key: <span className="font-mono">{settingForm.key}</span></p>
              ) : null}
            </div>

                <div className="grid gap-2">
              <Label>Type</Label>
              <Select value={settingForm.itemKey} onValueChange={(value) => setSettingForm((current) => ({ ...current, itemKey: value }))} disabled={editingItem != null || !selectedOption}>
                <SelectTrigger aria-label="Type" className="w-full">
                  <SelectValue placeholder={selectedOption ? 'Choose type' : 'Choose category first'} />
                </SelectTrigger>
                <SelectContent>
                  {(selectedOption?.items ?? []).map((option) => (
                    <SelectItem key={option.key} value={option.key}>
                      {option.text}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {settingForm.itemKey ? (
                <p className="text-xs text-muted-foreground">Key: <span className="font-mono">{settingForm.itemKey}</span></p>
              ) : null}
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-border/70 bg-muted/15 p-4">
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">Value</p>
                {selectedOption?.text ? <Badge variant="outline">{selectedOption.text}</Badge> : null}
              </div>
              <div className="mt-4 grid gap-2">
              <Label htmlFor={`${mode}_value`}>Value</Label>
              <Input id={`${mode}_value`} value={settingForm.value} onChange={(event) => setSettingForm((current) => ({ ...current, value: event.target.value }))} />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={resetDialog} disabled={saveMutation.isPending}>Cancel</Button>
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? 'Saving...' : editingItem ? `Update ${title}` : `Save ${title}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}